import { useSyncExternalStore } from 'react';
import { z } from 'zod';
import { ApiError } from '@packages/api';
import type { AddToastInput } from '../components/ui/Toast';
import { CLIENT_VERSION } from './config';

/**
 * Tells an open tab whether a newer Bill bundle has been deployed since it loaded.
 *
 * A tab keeps running the bundle it loaded until something reloads it, so after a
 * deploy it can keep sending requests the backend no longer accepts. Nothing in
 * the UI announces a deploy: the tab reloads itself on its next navigation
 * (`useReloadOnNavigationWhenStale`) and only says so when a save fails because
 * of it (`isStaleBundleRejection`).
 *
 * The build publishes its version at `/version.json` (`vite.config.ts`).
 */

const DeployedVersionSchema = z.object({ version: z.string().min(1) });

/** Checks closer together than this reuse the previous answer. */
export const VERSION_CHECK_MIN_INTERVAL_MS = 60_000;

/** How often a visible tab checks on its own. */
export const VERSION_CHECK_POLL_INTERVAL_MS = 5 * 60_000;

interface VersionMonitorOptions {
  currentVersion: string;
  fetchDeployedVersion: () => Promise<string | null>;
  now?: () => number;
}

export interface VersionMonitor {
  /** True once a check has seen a different deployed version. Never reverts. */
  isStale: () => boolean;
  /** Resolves to `isStale()` after checking, unless a check ran within the minimum interval. */
  check: (options?: { force?: boolean }) => Promise<boolean>;
  subscribe: (listener: () => void) => () => void;
}

export function createVersionMonitor({ currentVersion, fetchDeployedVersion, now = Date.now }: VersionMonitorOptions): VersionMonitor {
  let stale = false;
  let lastCheckedAt = Number.NEGATIVE_INFINITY;
  let inflight: Promise<boolean> | null = null;
  const listeners = new Set<() => void>();

  const check = ({ force = false }: { force?: boolean } = {}): Promise<boolean> => {
    if (stale) return Promise.resolve(true);
    if (inflight) return inflight;
    if (!force && now() - lastCheckedAt < VERSION_CHECK_MIN_INTERVAL_MS) return Promise.resolve(false);
    lastCheckedAt = now();
    inflight = fetchDeployedVersion()
      .then((deployed) => {
        // An unreadable answer is not evidence of a deploy; stay as we were.
        if (deployed !== null && deployed !== currentVersion) {
          stale = true;
          listeners.forEach((listener) => listener());
        }
        return stale;
      })
      .finally(() => {
        inflight = null;
      });
    return inflight;
  };

  return {
    isStale: () => stale,
    check,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const response = await fetch('/version.json', { cache: 'no-store' });
    if (!response.ok) return null;
    const parsed = DeployedVersionSchema.safeParse(await response.json());
    return parsed.success ? parsed.data.version : null;
  } catch {
    // Offline, or the host answered with something other than JSON: no answer.
    return null;
  }
}

/** Local builds have no published version to compare against. */
const versionChecksEnabled = CLIENT_VERSION !== 'localhost';

export const appVersion: VersionMonitor = versionChecksEnabled
  ? createVersionMonitor({ currentVersion: CLIENT_VERSION, fetchDeployedVersion })
  : { isStale: () => false, check: () => Promise.resolve(false), subscribe: () => () => undefined };

/**
 * Checks when the tab comes back into view and on a slow poll while it is visible.
 * Call once at startup.
 */
export function installVersionChecks(): void {
  if (!versionChecksEnabled) return;
  const checkIfVisible = () => {
    if (document.visibilityState === 'visible') void appVersion.check();
  };
  document.addEventListener('visibilitychange', checkIfVisible);
  window.addEventListener('focus', checkIfVisible);
  window.setInterval(checkIfVisible, VERSION_CHECK_POLL_INTERVAL_MS);
}

export function useIsBundleStale(): boolean {
  return useSyncExternalStore(appVersion.subscribe, appVersion.isStale);
}

/**
 * A request the backend rejected as malformed. From a current bundle that is a
 * bug; from a stale one it is usually a contract the deploy changed, so callers
 * pair this with a version check before telling the user to reload.
 */
export function isRequestValidationError(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'VALIDATION_ERROR';
}

/** Whether `error` should be explained as "reload to pick up the new version". */
export function isStaleBundleRejection(error: unknown, bundleStale: boolean): boolean {
  return bundleStale && isRequestValidationError(error);
}

export const STALE_BUNDLE_TITLE = 'Bill has been updated';

/** The global mutation toast for a stale-bundle rejection. */
export function staleBundleToast(): AddToastInput {
  return {
    variant: 'warning',
    title: STALE_BUNDLE_TITLE,
    description: "That change wasn't saved. Reload to get the latest version, then try again.",
    action: { label: 'Reload', onClick: () => window.location.reload() },
    duration: 0,
  };
}
export const STALE_BUNDLE_SAVE_MESSAGE = "Bill has been updated since this page loaded. Your changes weren't saved.";
